import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { useLocale } from "../../context/LocaleContext";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";

const SUPPORT_EMAIL = "support@vaultgrid.net";
const SUPPORT_INSTAGRAM = "https://www.instagram.com/vaultgrid_official/";
const REASON_CODES = new Set([
  "community_reports",
  "platform_safety_review",
  "community_rules_violation",
  "unauthorized_guests",
  "unwanted_contact",
  "integrity_or_trust",
  "legal_or_security",
]);

const BlockedAccountScreen = ({ blockReason = "", decision: initialDecision = null }) => {
  const { t } = useTranslation("common");
  const { isRussianContour } = useLocale();
  const [decision, setDecision] = useState(initialDecision);
  const [erasureModalOpen, setErasureModalOpen] = useState(false);
  const [erasurePending, setErasurePending] = useState(false);
  const [erasureSubmitting, setErasureSubmitting] = useState(false);
  const [erasureError, setErasureError] = useState("");
  const messageKey = REASON_CODES.has(blockReason)
    ? `blocked_account.reasons.${blockReason}`
    : "blocked_account.message";

  useEffect(() => {
    let active = true;
    api
      .get("/accounts/moderation/decision/", { suppressGlobalError: true })
      .then((response) => {
        if (active && response.data?.decision) setDecision(response.data.decision);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    api.get("/accounts/account-erasure-request/", { meta: { suppressGlobalError: true } })
      .then((response) => {
        if (active) setErasurePending(Boolean(response.data?.pending));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const submitErasureRequest = async () => {
    if (erasureSubmitting) return;
    setErasureSubmitting(true);
    setErasureError("");
    try {
      await api.post(
        "/accounts/account-erasure-request/",
        { confirmation: "REQUEST_ERASURE" },
        { meta: { suppressGlobalError: true } },
      );
      setErasurePending(true);
      setErasureModalOpen(false);
    } catch {
      setErasureError(t("blocked_account.erasure_error"));
    } finally {
      setErasureSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] h-[100dvh] w-screen overflow-y-auto bg-gradient-to-b from-stone-950 to-slate-900 px-4"
      style={{
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 32px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)",
      }}
    >
      <main className="mx-auto w-full max-w-2xl rounded-3xl border border-red-500/30 bg-black/50 p-6 text-left shadow-[0_16px_60px_rgba(0,0,0,0.5)] sm:p-8">
        <h1 className="text-center text-2xl font-semibold tracking-tight text-white sm:text-3xl">
          {t("blocked_account.title")}
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base">{t(messageKey)}</p>

        {decision?.summary ? (
          <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-200">
            <h2 className="font-semibold text-white">{t("blocked_account.reason_details_title")}</h2>
            <p className="mt-3 leading-relaxed">{decision.summary}</p>
          </section>
        ) : null}

        {decision?.paid_purchase_review ? (
          <section className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4">
            <h2 className="font-semibold text-amber-100">{t("blocked_account.paid_review_title")}</h2>
            <p className="mt-2 text-sm text-zinc-300">{t("blocked_account.paid_review_body")}</p>
          </section>
        ) : null}

        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <h2 className="font-semibold text-white">{t("blocked_account.review_title")}</h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-300">{t("blocked_account.review_body")}</p>
        </section>

        <nav className="mt-6 grid gap-2 text-sm sm:grid-cols-2">
          {isRussianContour ? (
            <>
              <a href="/legal/terms?from=blocked" className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sky-300 hover:bg-white/10">
                {t("blocked_account.public_offer")}
              </a>
              <a href="/legal/privacy?from=blocked" className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sky-300 hover:bg-white/10">
                {t("blocked_account.privacy_policy")}
              </a>
            </>
          ) : (
            <>
              <a href="/legal/community-rules?from=blocked" className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sky-300 hover:bg-white/10">
                {t("blocked_account.community_rules")}
              </a>
              <a href="/legal/moderation?from=blocked" className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sky-300 hover:bg-white/10">
                {t("blocked_account.moderation_policy")}
              </a>
            </>
          )}
        </nav>

        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-200">
          <h2 className="font-semibold text-white">{t("blocked_account.erasure_title")}</h2>
          <p className="mt-2 leading-relaxed">{t("blocked_account.erasure_body")}</p>
          <button
            type="button"
            className="mt-4 min-h-11 w-full rounded-xl bg-white px-4 py-3 font-semibold text-slate-900 transition hover:bg-slate-100 disabled:cursor-default disabled:bg-white/15 disabled:text-white/70"
            onClick={() => { setErasureError(""); setErasureModalOpen(true); }}
            disabled={erasurePending}
          >
            {t(erasurePending ? "blocked_account.erasure_submitted" : "blocked_account.erasure_button")}
          </button>
          {erasurePending ? (
            <p role="status" className="mt-3 text-sm leading-relaxed text-emerald-200">
              {t("blocked_account.erasure_received")}
            </p>
          ) : null}
        </section>

        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="mt-3 block rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-sky-300 hover:bg-white/10"
        >
          {t("blocked_account.email_label")}: {SUPPORT_EMAIL}
        </a>
        <a
          href={SUPPORT_INSTAGRAM}
          target="_blank"
          rel="noreferrer"
          className="mt-2 block rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-sm text-sky-300 hover:bg-white/10"
        >
          {t("blocked_account.instagram_label")}: @vaultgrid_official
        </a>
      </main>
      <AppModalShell
        open={erasureModalOpen}
        onClose={() => { if (!erasureSubmitting) setErasureModalOpen(false); }}
        title={t("blocked_account.erasure_modal_title")}
        description={t("blocked_account.erasure_modal_body")}
        closeAriaLabel={t("blocked_account.erasure_cancel")}
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <AppModalButton variant="secondary" onClick={() => setErasureModalOpen(false)} disabled={erasureSubmitting}>
              {t("blocked_account.erasure_cancel")}
            </AppModalButton>
            <AppModalButton variant="danger" onClick={submitErasureRequest} loading={erasureSubmitting}>
              {t("blocked_account.erasure_confirm")}
            </AppModalButton>
          </div>
        }
      >
        {erasureError ? <p role="alert" className="text-sm text-red-200">{erasureError}</p> : null}
      </AppModalShell>
    </div>
  );
};

export default BlockedAccountScreen;
